import { SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NotificationStore } from '@indutrust/notification-data';
import type { EventNotification } from '@indutrust/notification-model';
import {
  isSameTabLinkClick,
  mapNotificationToViewModel,
  provideTranslocoScopeNotification,
} from '@indutrust/notification-util';
import { EmptyStateComponent } from '@indutrust/shared-ui';
import { TranslocoDirective } from '@jsverse/transloco';
import { Badge } from 'primeng/badge';
import { ButtonDirective } from 'primeng/button';
import { Popover } from 'primeng/popover';

/**
 * Compact notification popover shown from the topbar bell icon.
 *
 * Displays the 5 most recent notifications with per-type icons, a
 * "Mark all as read" button, and a link to the full `/notifications` page.
 *
 * @example
 * <idt-notification-panel #panel />
 * <button (click)="panel.toggle($event)">Bell</button>
 */
@Component({
  selector: 'idt-notification-panel',
  imports: [
    Badge,
    ButtonDirective,
    EmptyStateComponent,
    Popover,
    RouterLink,
    SlicePipe,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t; prefix: 'notification'">
      <p-popover
        #popover
        appendTo="body"
        styleClass="no-popover-arrow topbar-notification-panel">
        <div class="notification-panel flex flex-col">
          <!-- Header -->
          <div
            class="notification-panel-header shrink-0 flex items-center
              justify-between min-h-11 pb-3 mb-1 border-b border-surface">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-base text-color">{{
                t('title')
              }}</span>
              @if (store.unreadCount() > 0) {
                <p-badge
                  severity="danger"
                  size="small"
                  [value]="store.unreadCount()" />
              }
            </div>
            @if (store.hasUnread()) {
              <button
                pButton
                size="small"
                type="button"
                [link]="true"
                (click)="store.markAllAsRead()">
                {{ t('markAllRead') }}
              </button>
            }
          </div>

          <!-- Scrollable body -->
          <div class="notification-panel-body">
            @if (recentNotificationItems().length === 0) {
              <idt-empty-state
                icon="bell-slash"
                variant="inline"
                [title]="t('empty')" />
            } @else {
              <ul class="flex flex-col gap-1 list-none m-0 p-0 py-2">
                @for (item of recentNotificationItems(); track item.id) {
                  @if (item.route) {
                    <li>
                      <a
                        class="flex items-start gap-3 px-3 py-2 rounded-border
                        transition-colors hover:bg-emphasis cursor-pointer
                        no-underline visited:text-color outline-none
                        focus-visible:ring-2 focus-visible:ring-primary"
                        [class.bg-highlight]="!item.read"
                        [class.text-muted-color]="item.read"
                        [routerLink]="item.route"
                        (click)="onNotificationLinkClick($event, item.source)">
                        <span
                          [class]="
                            'flex items-center justify-center size-8 rounded-full shrink-0 mt-1 ' +
                            item.badgeClass
                          ">
                          <i [class]="'pi text-sm! ' + item.iconName"></i>
                        </span>
                        <div class="flex flex-col gap-1 flex-1 min-w-0">
                          <span
                            class="text-sm text-color leading-snug"
                            [class.font-semibold]="!item.read"
                            [class.text-color]="!item.read"
                            [class.text-muted-color]="item.read">
                            {{ t('types.' + item.typeLabelKey) }}
                          </span>
                          @if (item.message) {
                            <span
                              class="text-xs text-muted-color leading-snug line-clamp-2">
                              {{ item.message }}
                            </span>
                          } @else if (item.hasI18nDescription) {
                            <span
                              class="text-xs text-muted-color leading-snug line-clamp-2">
                              {{
                                t(
                                  'descriptions.' + item.typeLabelKey,
                                  item.descriptionParams
                                )
                              }}
                            </span>
                          }
                          @if (item.createdAt) {
                            <span class="text-xs text-muted-color">{{
                              item.createdAt | slice: 0 : 10
                            }}</span>
                          }
                        </div>
                        @if (!item.read) {
                          <span
                            class="flex items-center gap-1 shrink-0 mt-1 text-primary"
                            data-testid="notification-unread-marker">
                            <i
                              aria-hidden="true"
                              class="pi pi-circle-fill text-xs!"></i>
                            <span class="text-xs font-semibold">{{
                              t('unread')
                            }}</span>
                          </span>
                        }
                      </a>
                    </li>
                  } @else {
                    <li
                      class="flex items-start gap-3 px-3 py-2 rounded-border"
                      [class.bg-highlight]="!item.read">
                      <span
                        [class]="
                          'flex items-center justify-center size-8 rounded-full shrink-0 mt-1 ' +
                          item.badgeClass
                        ">
                        <i [class]="'pi text-sm! ' + item.iconName"></i>
                      </span>
                      <div class="flex flex-col gap-1 flex-1 min-w-0">
                        <span
                          class="text-sm leading-snug"
                          [class.font-semibold]="!item.read"
                          [class.text-color]="!item.read"
                          [class.text-muted-color]="item.read">
                          {{ t('types.' + item.typeLabelKey) }}
                        </span>
                        @if (item.message) {
                          <span
                            class="text-xs text-muted-color leading-snug line-clamp-2">
                            {{ item.message }}
                          </span>
                        } @else if (item.hasI18nDescription) {
                          <span
                            class="text-xs text-muted-color leading-snug line-clamp-2">
                            {{
                              t(
                                'descriptions.' + item.typeLabelKey,
                                item.descriptionParams
                              )
                            }}
                          </span>
                        }
                        @if (item.createdAt) {
                          <span class="text-xs text-muted-color">{{
                            item.createdAt | slice: 0 : 10
                          }}</span>
                        }
                      </div>
                      @if (!item.read) {
                        <span
                          class="flex items-center gap-1 shrink-0 mt-1 text-primary"
                          data-testid="notification-unread-marker">
                          <i
                            aria-hidden="true"
                            class="pi pi-circle-fill text-xs!"></i>
                          <span class="text-xs font-semibold">{{
                            t('unread')
                          }}</span>
                        </span>
                      }
                    </li>
                  }
                }
              </ul>
            }
          </div>

          <!-- Footer -->
          <div class="shrink-0 pt-2 mt-1 border-t border-surface">
            <a
              class="flex items-center justify-center gap-1.5 text-sm text-primary hover:underline cursor-pointer py-1"
              routerLink="/notifications"
              (click)="popover.hide()">
              {{ t('seeAll') }}
              <i class="pi pi-arrow-right text-xs!"></i>
            </a>
          </div>
        </div>
      </p-popover>
    </ng-container>
  `,
  providers: [provideTranslocoScopeNotification()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationPanelComponent {
  readonly store = inject(NotificationStore);

  readonly popoverRef = viewChild<Popover>('popover');

  protected readonly recentNotificationItems = computed(() =>
    this.store
      .notifications()
      .slice(0, 5)
      .map((notification) => mapNotificationToViewModel(notification, 'panel'))
  );

  protected onNotificationLinkClick(
    event: MouseEvent,
    notification: EventNotification
  ): void {
    this.store.markAsRead(notification.id);

    if (isSameTabLinkClick(event)) {
      this.popoverRef()?.hide();
    }
  }

  /** Opens or closes the notification popover. */
  toggle(event: Event): void {
    this.store.loadAll();
    this.popoverRef()?.toggle(event);
  }
}
